import { WorldPrefsSchema } from "@uzverse/contracts";
import type { WorldPrefs } from "@uzverse/contracts";

export type { WorldPrefs };

/**
 * World view preferences (camera mode, quality tier, dock open) as user settings on the gateway
 * (`PATCH /api/v1/settings { world }`), so they follow the user across devices. localStorage stays
 * the instant cache the first paint reads; the server value wins once it arrives, and a value
 * only this device has is pushed up (first run after the setting appeared).
 */
export interface PrefsTransport {
  get(): Promise<WorldPrefs>;
  patch(patch: WorldPrefs): Promise<void>;
}

const SETTINGS_URL = "/api/v1/settings";

export const httpPrefs: PrefsTransport = {
  async get() {
    const res = await fetch(SETTINGS_URL, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`settings: ${res.status}`);
    const body = (await res.json()) as { settings?: { world?: unknown } };
    const parsed = WorldPrefsSchema.safeParse(body.settings?.world ?? {});
    return parsed.success ? parsed.data : {};
  },
  async patch(patch) {
    const res = await fetch(SETTINGS_URL, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ world: patch }),
    });
    if (!res.ok) throw new Error(`settings: ${res.status}`);
  },
};

/** The in-browser fake gateway keeps the prefs in memory (reads through `window.__uzMockPrefs`). */
export function createMemoryPrefs(initial: WorldPrefs = {}): PrefsTransport & {
  data(): WorldPrefs;
} {
  let data: WorldPrefs = { ...initial };
  return {
    get: () => Promise.resolve({ ...data }),
    patch: (patch) => {
      data = { ...data, ...patch };
      return Promise.resolve();
    },
    data: () => ({ ...data }),
  };
}

function defaultTransport(): PrefsTransport | null {
  if (import.meta.env.MODE === "test") return null;
  if (import.meta.env["VITE_UZ_MOCK"] === "1") {
    const mock = createMemoryPrefs();
    (globalThis as { __uzMockPrefs?: () => WorldPrefs }).__uzMockPrefs = () => mock.data();
    return mock;
  }
  return httpPrefs;
}

let transport: PrefsTransport | null = defaultTransport();

/** Tests inject a transport (or null to switch the server side off). */
export function setPrefsTransport(next: PrefsTransport | null): void {
  transport = next;
}

/** Fire and forget: the cache already holds the value, so a failed save only loses the sync. */
export function pushWorldPref(patch: WorldPrefs): void {
  void transport?.patch(patch).catch(() => undefined);
}

/**
 * Reads the server's prefs and merges them with this device's cache: the server wins where it has
 * a value, and keys only the cache has are saved to the server. Resolves to the effective prefs;
 * on any failure to the cache alone.
 */
export async function syncWorldPrefs(local: WorldPrefs): Promise<WorldPrefs> {
  if (!transport) return local;
  try {
    const remote = await transport.get();
    const merged: WorldPrefs = { ...local, ...remote };
    const missing: WorldPrefs = {};
    if (remote.cameraMode === undefined && local.cameraMode !== undefined) {
      missing.cameraMode = local.cameraMode;
    }
    if (remote.quality === undefined && local.quality !== undefined)
      missing.quality = local.quality;
    if (remote.dockOpen === undefined && local.dockOpen !== undefined) {
      missing.dockOpen = local.dockOpen;
    }
    if (remote.layout === undefined && local.layout !== undefined) missing.layout = local.layout;
    if (remote.timeOfDay === undefined && local.timeOfDay !== undefined) {
      missing.timeOfDay = local.timeOfDay;
    }
    if (remote.weather === undefined && local.weather !== undefined)
      missing.weather = local.weather;
    if (Object.keys(missing).length > 0) pushWorldPref(missing);
    return merged;
  } catch {
    return local;
  }
}
